/**
 * Shared types & constants between web (apps/web) and api (apps/api).
 * Update this file when you need types/utilities available to both packages.
 *
 * Phase 1A: 7-table D1 schema TypeScript interfaces per
 * tm-ai-workspace/plan/funpaper-app/03-data-model.md §4 + §6
 */

export const APP_VERSION = '0.1.140' as const;

export type AppEnv = 'development' | 'remote-dev' | 'production' | 'unknown';

export type AppInfo = {
  name: string;
  env: AppEnv | string;
  version: string;
  docs?: string;
};

export type HealthResponse = {
  status: 'ok' | 'degraded' | 'down';
  app: string;
  env: string;
  version: string;
  timestamp: string;
};

export type ApiError = {
  error: string;
  message?: string;
  path?: string;
  method?: string;
};

export const API_PATHS = {
  root: '/',
  health: '/health',
  version: '/version',
  dbTables: '/db/tables',
  worksheets: '/worksheets',
  worksheetsStats: '/worksheets/stats',
} as const;

// ============================================================================
// Domain enums (doc 03 §6)
// ============================================================================

export type SwipeDirection = 'approve' | 'reject' | 'save' | 'drag_no_commit';
export type DifficultyPref = 'easy' | 'balanced' | 'challenging';
export type FrequencyPref = 'daily' | '3x_week' | '5x_week' | 'custom';
export type PrintStatus = 'none' | 'printed' | 'saved' | 'shared_pdf' | 'shared_wa';
export type RejectReason =
  | 'tema'
  | 'difficulty_easy'
  | 'difficulty_hard'
  | 'boring'
  | 'duplicate'
  | 'other';
export type ClassId = 1 | 2 | 3; // 1=PG, 2=TK A, 3=TK B
export type AgeBracket = 2 | 3 | 4 | 5 | 6;
// Phase 2 task #6: 'count' (worksheet count + frequency combo)
// Phase 2 task #7: 'commitment' (pact signed flag)
export type QuizQuestionKey =
  | 'goals'
  | 'tema'
  | 'activity'
  | 'difficulty'
  | 'frequency'
  | 'count'
  | 'commitment';
export type QuizQuestionOrder = 1 | 2 | 3 | 4 | 5;
export type DailyCount = 1 | 2 | 3 | 4 | 5;
export type SwapCount = 1 | 2 | 3;

// ============================================================================
// Table 1: child_profile
// ============================================================================

export interface ChildProfile {
  id: number;
  deviceId: string;
  name: string;
  age: AgeBracket;
  classId: ClassId;
  avatarId: number;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChildProfileInsert
  extends Omit<ChildProfile, 'id' | 'createdAt' | 'updatedAt' | 'isDefault'> {
  id?: number;
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// ============================================================================
// Table 2: user_preferences
// ============================================================================

export interface UserPreferences {
  id: number;
  deviceId: string;
  childProfileId: number;
  preferredTema: string[];
  preferredActivity: string[];
  dailyCount: DailyCount;
  difficulty: DifficultyPref;
  frequency: FrequencyPref;
  goals: string[];
  commitmentPactSigned: boolean;
  /** Kelompok usia preferensi untuk rekomendasi (plan 21): 1=PG, 2=TK A,
   *  3=TK B. null = ikuti kelas profil anak (default). */
  preferredClassId: ClassId | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserPreferencesInsert
  extends Omit<UserPreferences, 'id' | 'createdAt' | 'updatedAt'> {
  id?: number;
  createdAt?: string;
  updatedAt?: string;
}

// ============================================================================
// Table 3: quiz_answers
// ============================================================================

/**
 * Quiz answer payload — flexible shape per questionKey:
 *  - tema / activity / goals: reads `selected` (additive union)
 *  - difficulty / frequency:  reads `selected[0]` (single-value)
 *  - count (Phase 2 #6):     reads `count` (1-5) + `frequency` (enum)
 *  - commitment (Phase 2 #7): reads `commitment` (boolean)
 *
 * `selected` is always present (defaults to []) for backwards
 * compat with older quiz screens. The new fields are optional.
 */
export interface QuizAnswerPayload {
  selected: string[];
  skipped: boolean;
  timeSpentMs: number;
  customText?: string;
  /** Phase 2 task #6 — worksheet count slider value (1-5) */
  count?: DailyCount;
  /** Phase 2 task #6 — frequency chip value */
  frequency?: FrequencyPref;
  /** Phase 2 task #7 — commitment pact signed flag */
  commitment?: boolean;
}

export interface QuizAnswer {
  id: number;
  deviceId: string;
  childProfileId: number;
  questionKey: QuizQuestionKey;
  questionOrder: QuizQuestionOrder;
  answerPayload: QuizAnswerPayload;
  answeredAt: string;
}

/**
 * Server derives `deviceId` from the X-Device-Id header (set by
 * apiFetch from localStorage / SecureStore), so the request body
 * does NOT carry it. Insert type reflects that contract.
 */
export interface QuizAnswerInsert
  extends Omit<QuizAnswer, 'id' | 'answeredAt' | 'deviceId'> {
  id?: number;
  answeredAt?: string;
}

// ============================================================================
// Table 4: daily_offers
// ============================================================================

export interface DailyOffer {
  id: number;
  deviceId: string;
  childProfileId: number;
  offerDate: string; // YYYY-MM-DD
  worksheetIds: number[];
  algoVersion: string;
  computedAt: string;
  expiresAt: string;
}

export interface DailyOfferInsert
  extends Omit<DailyOffer, 'id' | 'computedAt'> {
  id?: number;
  computedAt?: string;
}

// ============================================================================
// Table 5: swipe_log
// ============================================================================

export interface SwipeLog {
  id: number;
  deviceId: string;
  childProfileId: number;
  offerId: number | null;
  worksheetId: number;
  direction: SwipeDirection;
  velocity: number | null;
  distance: number | null;
  timeToDecideMs: number | null;
  rejectReason: RejectReason | null;
  rejectReasonText: string | null;
  undoCount: 0 | 1;
  undoedAt: string | null;
  swipedAt: string;
}

export interface SwipeLogInsert
  extends Omit<SwipeLog, 'id' | 'swipedAt' | 'undoCount' | 'undoedAt'> {
  id?: number;
  undoCount?: 0 | 1;
  undoedAt?: string | null;
  swipedAt?: string;
}

// ============================================================================
// Table 6: approved_worksheets
// ============================================================================

export interface ApprovedWorksheet {
  id: number;
  deviceId: string;
  childProfileId: number;
  worksheetId: number;
  offerId: number | null;
  approvedAt: string;
  completedAt: string | null;
  printStatus: PrintStatus;
  completionFeedback: string | null;
}

export interface ApprovedWorksheetInsert
  extends Omit<ApprovedWorksheet, 'id' | 'approvedAt' | 'printStatus'> {
  id?: number;
  approvedAt?: string;
  printStatus?: PrintStatus;
}

// ============================================================================
// Table 7: swap_log
// ============================================================================

export interface SwapLog {
  id: number;
  deviceId: string;
  childProfileId: number;
  rejectedWorksheetId: number;
  replacementWorksheetId: number;
  swapCountToday: SwapCount;
  reason: RejectReason | null;
  reasonText: string | null;
  swipedAt: string;
}

export interface SwapLogInsert
  extends Omit<SwapLog, 'id' | 'swipedAt'> {
  id?: number;
  swipedAt?: string;
}

// ============================================================================
// Composite types (Phase 1C API responses)
// ============================================================================

export interface WorksheetMeta {
  id: number;
  name: string;
  theme: string;
  activity: string;
  ageRange: string;
  thumbnailUrl: string;
  pdfUrl: string;
  difficulty: string;
  estimatedDurationMinutes: number;
  learningObjective: string;
  skills: string[];
  aiExplanation: string;
  isHero?: boolean;
  isWildcard?: boolean;
}

export interface SwipeResult {
  success: boolean;
  newWorksheet?: WorksheetMeta;
  swapCount: number;
  undoToken: string;
  undoExpiresAt: string;
}

// ============================================================================
// Plan 16 Phase C — paywall feature flag + premium teaser constants
// ============================================================================

/**
 * Feature flag paywall (plan 16). `false` = free-first launch; teaser
 * "Segera Hadir" tampil tapi TIDAK ada flow pembayaran apa pun.
 */
export const PAYWALL_ENABLED = false as const;

/** Jumlah worksheet tematik premium yang dipakai copy teaser (display only). */
export const PREMIUM_CATALOG_TEASER_COUNT = '2.500+';

// ============================================================================
// Plan 17 — guest trial (deferred login)
// ============================================================================

/** Lama trial guest (hari) sejak guest account dibuat. */
export const GUEST_TRIAL_DAYS = 3;

/** Prefix google_sub sentinel untuk guest account (bukan akun Google asli). */
export const GUEST_SUB_PREFIX = 'guest:';

// ============================================================================
// Plan 19 — menu Belanja & premium credits
// ============================================================================

/** Saldo awal satu kali untuk account Google yang baru dibuat. */
export const SIGNUP_CREDIT_AMOUNT = 50;

/** 1 point = 1 worksheet unlock. */
export const CREDIT_PER_WORKSHEET = 1;

/** Biaya credit per AI bundle yang berhasil dibuat. Angka final = Fase 0. */
export const AI_BUNDLE_CREDIT_COST = 10;

/** Product code entitlement Calistung (selaras doc 18). */
export const CALISTUNG_PREMIUM_PRODUCT = 'calistung_30_hari';

/** Product code for the unified premium worksheet recommendation catalog. */
export const WORKSHEET_CATALOG_PREMIUM_PRODUCT = 'worksheet_catalog_premium';
